import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { axiosForAPI } from '../helper_functions/utilities';
import {
    DISPLAY_REGION_TO_PLATFORM,
    MIN_SUMMONER_GAME_NAME_LENGTH,
    parseSummonerSearchInput,
} from '../helper_functions/searchRouting';
import Seo from './Seo';
import ProfileIconWithFallback from '../shared/ProfileIcon';
import ChampionIconWithFallback from '../shared/ChampionIcon';
import {
    buildSearchChampionIconSrc,
    buildSearchProfileIconSrc,
    buildSearchTierIconSrc,
    buildSearchRoleIconSrc,
    formatSearchMasteryPoints,
    formatSearchEloLabel,
} from './search/searchHelpers';

const SearchResults = ({
    globallyUpdateDisplayedRegion,
    setSummonerSearchFormData,
    navigateToSearchResults,
}) => {
    const navigate = useNavigate();
    const params = useParams();
    const [searchParams] = useSearchParams();
    const [results, setResults] = useState([]);
    const [championResultsMeta, setChampionResultsMeta] = useState(null);
    const [loading, setLoading] = useState(true);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [error, setError] = useState('');

    const rawQuery = searchParams.get('q') || '';
    const searchMode = searchParams.get('mode') === 'champions' ? 'champions' : 'players';
    const currentPage = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1);
    const displayRegion = (params.displayRegion || 'na').toLowerCase();
    const platform = DISPLAY_REGION_TO_PLATFORM[displayRegion] || 'na1';

    useEffect(() => {
        globallyUpdateDisplayedRegion(platform);
    }, [globallyUpdateDisplayedRegion, platform]);

    useEffect(() => {
        setSummonerSearchFormData(parseSummonerSearchInput(rawQuery));
    }, [rawQuery, setSummonerSearchFormData]);

    useEffect(() => {
        const trimmedQuery = rawQuery.trim();
        if (!trimmedQuery) {
            setResults([]);
            setLoading(false);
            setHasLoaded(true);
            setError('');
            return undefined;
        }

        const parsedQuery = parseSummonerSearchInput(trimmedQuery);
        if (searchMode !== 'champions' && parsedQuery.gameName.length < MIN_SUMMONER_GAME_NAME_LENGTH) {
            setResults([]);
            setLoading(false);
            setHasLoaded(true);
            setError(`Summoner searches require at least ${MIN_SUMMONER_GAME_NAME_LENGTH} game-name characters.`);
            return undefined;
        }

        const abortController = new AbortController();

        async function loadResults() {
            try {
                setLoading(true);
                setError('');
                if (searchMode === 'champions') {
                    const response = await axiosForAPI.get('/leaderboards/champion-players/', {
                        params: {
                            platform,
                            query: trimmedQuery,
                            page: currentPage,
                            page_size: 20,
                        },
                        signal: abortController.signal,
                    });
                    setChampionResultsMeta(response.data);
                    setResults(response.data?.results || []);
                } else {
                    const response = await axiosForAPI.get('/summoner-search/', {
                        params: {
                            platform,
                            query: trimmedQuery,
                            limit: 25,
                        },
                        signal: abortController.signal,
                    });
                    setChampionResultsMeta(null);
                    setResults(response.data?.results || []);
                }
            } catch (requestError) {
                if (axios.isCancel(requestError) || requestError?.code === 'ERR_CANCELED') {
                    return;
                }
                setError('Unable to load results right now.');
                setResults([]);
                setChampionResultsMeta(null);
            } finally {
                setLoading(false);
                setHasLoaded(true);
            }
        }

        loadResults();

        return () => abortController.abort();
    }, [platform, rawQuery, searchMode, currentPage]);

    const trimmedQuery = rawQuery.trim();
    const directProfileHref = useMemo(() => {
        const parsed = parseSummonerSearchInput(rawQuery);
        if (!parsed.gameName || !parsed.tagLine) {
            return null;
        }
        return `/summoners/${displayRegion}/${parsed.gameName}-${parsed.tagLine}`;
    }, [displayRegion, rawQuery]);
    const championHeaderName = championResultsMeta?.champion?.name || trimmedQuery;

    function handleOpenResult(result) {
        navigate(`/summoners/${displayRegion}/${result.game_name}-${result.tag_line}`);
    }

    const titleQuery = trimmedQuery || 'Search';
    const seoTitle = `${titleQuery} Search Results | WINRATES.GG™`;
    const seoDescription = trimmedQuery
        ? searchMode === 'champions'
            ? `Browse top ${trimmedQuery} champion mastery players in ${displayRegion.toUpperCase()} on WINRATES.GG™.`
            : `Browse cached WINRATES.GG™ summoner results for ${trimmedQuery} in ${displayRegion.toUpperCase()}, including tier, preferred role, and most played champions.`
        : `Browse cached WINRATES.GG™ summoner search results in ${displayRegion.toUpperCase()}.`;

    return (
        <div className="mx-auto w-full max-w-screen-lg px-4 pt-6 pb-8 sm:pt-8 sm:pb-10">
            <Seo
                title={seoTitle}
                description={seoDescription}
                canonical={`/search/${displayRegion}?q=${encodeURIComponent(trimmedQuery)}`}
                structuredData={{
                    '@context': 'https://schema.org',
                    '@type': 'SearchResultsPage',
                    name: seoTitle,
                    description: seoDescription,
                    url: `https://winrates.gg/search/${displayRegion}?q=${encodeURIComponent(trimmedQuery)}`,
                }}
            />

            <header className="mb-5">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-cyan-400">
                    {searchMode === 'champions' ? 'Search Results' : 'Search Results For'}
                </div>
                <h1 className="mt-1 text-2xl font-semibold tracking-tight text-zinc-100 sm:text-3xl">
                    {searchMode === 'champions'
                        ? `Highest Mastery ${championHeaderName || 'Champion'} Players`
                        : trimmedQuery
                            ? `${trimmedQuery}`
                            : 'Enter a summoner query'}
                </h1>
                <div className="mt-2 flex flex-wrap items-center gap-x-1 gap-y-1 text-sm text-zinc-400">
                    {searchMode === 'champions' ? (
                        <>
                            {hasLoaded && !loading ? (
                                <span>{`${championResultsMeta?.total_results || 0} players`}</span>
                            ) : null}
                            {championResultsMeta?.champion?.name_id ? (
                                <button
                                    type="button"
                                    className="ml-3 rounded-md border border-cyan-800/60 bg-cyan-950/30 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-cyan-100 transition hover:border-cyan-700"
                                    onClick={() => navigate(`/analytics/champions/${championResultsMeta.champion.name_id}`)}
                                >
                                    View analytics
                                </button>
                            ) : null}
                        </>
                    ) : (
                        <>
                            <span>Player Search,</span>
                            {hasLoaded && !loading ? (
                                <span>{`${results.length} ${results.length === 1 ? 'result.' : 'results.'}`}</span>
                            ) : null}
                            <span>Sorted by Likeness, Tier, and LP.</span>
                        </>
                    )}
                </div>
            </header>

            <div className="space-y-3">
                {loading ? (
                    <div className="rounded-lg border border-[#33333a] bg-zinc-950 px-4 py-5 text-sm text-zinc-300">
                        Searching summoners…
                    </div>
                ) : null}

                {!loading && error ? (
                    <div className="rounded-lg border border-rose-800/70 bg-rose-950/45 px-4 py-5 text-sm text-rose-100">
                        {error}
                    </div>
                ) : null}

                {!loading && !error && !results.length ? (
                    <div className="rounded-lg border border-[#33333a] bg-zinc-950 px-4 py-5">
                        <div className="text-base font-semibold text-zinc-100">
                            {searchMode === 'champions' ? 'No champion players found.' : 'No cached summoners found.'}
                        </div>
                        <div className="mt-1 text-sm text-zinc-400">
                            {searchMode === 'champions'
                                ? 'Try a different champion spelling or switch back to player search.'
                                : 'Try a different spelling, a shorter partial name, or search the exact Riot ID directly.'}
                        </div>
                        {searchMode === 'players' && directProfileHref ? (
                            <button
                                type="button"
                                className="mt-4 inline-flex min-h-[40px] items-center justify-center rounded-lg border border-zinc-700 bg-zinc-900 px-4 text-xs font-semibold uppercase tracking-[0.16em] text-zinc-200 transition duration-150 hover:border-zinc-600 hover:bg-zinc-800"
                                onClick={() => navigate(directProfileHref)}
                            >
                                Search Riot Profile
                            </button>
                        ) : null}
                    </div>
                ) : null}

                {!loading && !error && results.length ? (
                    <div className="space-y-3">
                        {results.map((result) => {
                            const eloLabel = formatSearchEloLabel(result.competitive);
                            const isRanked = Boolean(result.competitive?.tier && result.competitive.tier !== 'UNRANKED');
                            const masteryEntries = (result.champion_masteries || []).slice(0, 3);

                            if (searchMode !== 'champions') {
                                return (
                                    <button
                                        key={`${result.puuid}-${result.platform_code}`}
                                        type="button"
                                        className="grid w-full gap-3 rounded-lg border border-[#33333a] bg-zinc-950 px-4 py-3 text-left transition duration-150 hover:border-zinc-600 hover:bg-zinc-900 md:grid-cols-[228px_68px_238px_136px] md:items-center md:justify-between md:gap-3"
                                        onClick={() => handleOpenResult(result)}
                                    >
                                        <div className="md:hidden">
                                            <div className="grid grid-cols-[minmax(0,1fr)_24px_36px_60px] items-center gap-3">
                                                <div className="flex min-w-0 items-center gap-3">
                                                    <div className="h-11 w-11 shrink-0 overflow-hidden rounded-lg border border-zinc-700">
                                                        <ProfileIconWithFallback
                                                            className="block h-full w-full object-cover"
                                                            src={buildSearchProfileIconSrc(result.profile_icon_id)}
                                                            alt="summoner profile icon"
                                                        />
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <div className="truncate text-sm font-semibold text-zinc-100">
                                                            {result.game_name}
                                                        </div>
                                                        <div className="mt-0.5 truncate text-[11px] uppercase tracking-[0.12em] text-zinc-400">
                                                            #{result.tag_line}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="flex justify-center">
                                                    {result.preferred_role ? (
                                                        <img
                                                            alt={`${result.preferred_role} role icon`}
                                                            className="h-[22px] w-[22px] opacity-90"
                                                            src={buildSearchRoleIconSrc(result.preferred_role)}
                                                        />
                                                    ) : null}
                                                </div>

                                                <div className="flex justify-center">
                                                    {masteryEntries[0] ? (
                                                        <div className="h-9 w-9 overflow-hidden rounded-full border-2 border-[#33333a]">
                                                            <ChampionIconWithFallback
                                                                className="h-full w-full scale-[1.18] rounded-full object-cover"
                                                                src={buildSearchChampionIconSrc(masteryEntries[0].name_id)}
                                                                alt={`${masteryEntries[0].name} icon`}
                                                            />
                                                        </div>
                                                    ) : null}
                                                </div>

                                                <div className="flex h-full flex-col items-end justify-between text-right">
                                                    {isRanked ? (
                                                        <>
                                                            <img
                                                                className="h-10 w-10 object-contain"
                                                                src={buildSearchTierIconSrc(result.competitive?.tier)}
                                                                alt={`${result.competitive?.tier} tier emblem`}
                                                            />
                                                            <div className="text-[10px] leading-4 text-zinc-300">
                                                                <div>{result.competitive.league_points} LP</div>
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-zinc-300">
                                                            Unranked
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="hidden min-w-0 items-center gap-3 md:flex md:w-[228px]">
                                            <div className="h-11 w-11 shrink-0 overflow-hidden rounded-lg border border-zinc-700">
                                                <ProfileIconWithFallback
                                                    className="block h-full w-full object-cover"
                                                    src={buildSearchProfileIconSrc(result.profile_icon_id)}
                                                    alt="summoner profile icon"
                                                />
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                                                    <span className="truncate text-sm font-semibold text-zinc-100">
                                                        {result.game_name}
                                                    </span>
                                                </div>
                                                <div className="mt-0.5 truncate text-[11px] uppercase tracking-[0.12em] text-zinc-400">
                                                    #{result.tag_line}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="hidden h-full items-center justify-start gap-2 md:flex md:w-[68px]">
                                            {result.preferred_role ? (
                                                <>
                                                <img
                                                    alt={`${result.preferred_role} role icon`}
                                                    className="h-4 w-4 shrink-0 opacity-90"
                                                    src={buildSearchRoleIconSrc(result.preferred_role)}
                                                />
                                                <span className="text-[12px] font-medium uppercase tracking-[0.1em] text-zinc-300">
                                                    {result.preferred_role.replace('_', ' ')}
                                                </span>
                                                </>
                                            ) : null}
                                        </div>

                                        <div className="hidden min-w-0 md:block md:w-[288px]">
                                            {masteryEntries.length ? (
                                                <div className="flex flex-wrap items-center gap-1.5 md:max-w-[288px]">
                                                    {masteryEntries.map((mastery) => (
                                                        <div
                                                            key={`${result.puuid}-${mastery.champion_id}`}
                                                            className="inline-flex items-center"
                                                        >
                                                            <div className="h-9 w-9 overflow-hidden rounded-full border-2 border-[#33333a]">
                                                                <ChampionIconWithFallback
                                                                    className="h-full w-full scale-[1.18] rounded-full object-cover"
                                                                    src={buildSearchChampionIconSrc(mastery.name_id)}
                                                                    alt={`${mastery.name} icon`}
                                                                />
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="text-[11px] text-zinc-500 md:text-left">
                                                    No mastery snapshot cached
                                                </div>
                                            )}
                                        </div>

                                        <div className="hidden shrink-0 md:block md:w-[136px] md:justify-self-end">
                                            {isRanked ? (
                                                <div className="flex items-center justify-end gap-2">
                                                    <div className="min-w-0 text-right">
                                                        <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-zinc-100">
                                                            {eloLabel}
                                                        </div>
                                                        <div className="mt-0.5 text-[11px] text-zinc-300">
                                                            {result.competitive.league_points} LP
                                                        </div>
                                                    </div>
                                                    <img
                                                        className="h-10 w-10 shrink-0 object-contain"
                                                        src={buildSearchTierIconSrc(result.competitive?.tier)}
                                                            alt={`${result.competitive?.tier} tier emblem`}
                                                    />
                                                </div>
                                            ) : (
                                                <div className="text-right text-[11px] font-semibold uppercase tracking-[0.1em] text-zinc-300">
                                                    Unranked
                                                </div>
                                            )}
                                        </div>
                                    </button>
                                );
                            }

                            return (
                                <button
                                    key={`${result.puuid}-${result.platform_code}`}
                                    type="button"
                                    className="grid w-full gap-3 rounded-lg border border-[#33333a] bg-zinc-950 px-4 py-3 text-left transition duration-150 hover:border-zinc-600 hover:bg-zinc-900 md:grid-cols-[228px_68px_238px_136px] md:items-center md:justify-between md:gap-3"
                                    onClick={() => handleOpenResult(result)}
                                >
                                    <div className="md:hidden">
                                        <div className="grid grid-cols-[minmax(0,1fr)_24px_52px_60px] items-center gap-3">
                                            <div className="flex min-w-0 items-center gap-3">
                                                <div className="h-11 w-11 shrink-0 overflow-hidden rounded-lg border border-zinc-700">
                                                    <ProfileIconWithFallback
                                                        className="block h-full w-full object-cover"
                                                        src={buildSearchProfileIconSrc(result.profile_icon_id)}
                                                        alt="summoner profile icon"
                                                    />
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <div className="truncate text-sm font-semibold text-zinc-100">
                                                        {result.game_name}
                                                    </div>
                                                    <div className="mt-0.5 truncate text-[11px] uppercase tracking-[0.12em] text-zinc-400">
                                                        #{result.tag_line}
                                                    </div>
                                                    <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                                                        {result.exact_match ? (
                                                            <span className="rounded-md border border-zinc-700 bg-zinc-900 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-zinc-200">
                                                                Exact
                                                            </span>
                                                        ) : null}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex justify-center">
                                                {result.preferred_role ? (
                                                    <img
                                                        alt={`${result.preferred_role} role icon`}
                                                        className="h-[22px] w-[22px] opacity-90"
                                                        src={buildSearchRoleIconSrc(result.preferred_role)}
                                                    />
                                                ) : null}
                                            </div>

                                            <div className="flex flex-col items-center justify-center gap-1">
                                                <div className="h-10 w-10 overflow-hidden rounded-full border-2 border-[#33333a]">
                                                    <ChampionIconWithFallback
                                                        className="h-full w-full scale-[1.18] rounded-full object-cover"
                                                        src={buildSearchChampionIconSrc(championResultsMeta?.champion?.name_id)}
                                                        alt={`${championResultsMeta?.champion?.name} icon`}
                                                    />
                                                </div>
                                                <div className="text-center text-[10px] leading-4 text-zinc-400">
                                                    {formatSearchMasteryPoints(result.champion_mastery_points)}
                                                </div>
                                            </div>

                                            <div className="flex h-full flex-col items-end justify-between text-right">
                                                {isRanked ? (
                                                    <>
                                                        <img
                                                            className="h-10 w-10 object-contain"
                                                            src={buildSearchTierIconSrc(result.competitive?.tier)}
                                                            alt={`${result.competitive?.tier || 'Unranked'} tier emblem`}
                                                        />
                                                        <div className="text-[10px] leading-4 text-zinc-300">
                                                            <div>{result.competitive.league_points} LP</div>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-zinc-300">
                                                        Unranked
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="hidden min-w-0 items-center gap-3 md:flex md:w-[228px]">
                                        <div className="h-11 w-11 shrink-0 overflow-hidden rounded-lg border border-zinc-700">
                                            <ProfileIconWithFallback
                                                className="block h-full w-full object-cover"
                                                src={buildSearchProfileIconSrc(result.profile_icon_id)}
                                                alt="summoner profile icon"
                                            />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                                                <span className="truncate text-sm font-semibold text-zinc-100">
                                                    {result.game_name}
                                                </span>
                                                {result.exact_match ? (
                                                    <span className="rounded-md border border-zinc-700 bg-zinc-900 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-zinc-200">
                                                        Exact
                                                    </span>
                                                ) : null}
                                            </div>
                                            <div className="mt-0.5 truncate text-[11px] uppercase tracking-[0.12em] text-zinc-400">
                                                #{result.tag_line}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="hidden h-full items-center justify-start gap-2 md:flex md:w-[68px]">
                                        {result.preferred_role ? (
                                            <>
                                            <img
                                                alt={`${result.preferred_role} role icon`}
                                                className="h-4 w-4 shrink-0 opacity-90"
                                                src={buildSearchRoleIconSrc(result.preferred_role)}
                                            />
                                            <span className="text-[12px] font-medium uppercase tracking-[0.1em] text-zinc-300">
                                                {result.preferred_role.replace('_', ' ')}
                                            </span>
                                            </>
                                        ) : null}
                                    </div>

                                    <div className="hidden min-w-0 md:block md:w-[238px]">
                                        <div className="inline-flex items-center gap-2">
                                            <div className="h-10 w-10 overflow-hidden rounded-full border-2 border-[#33333a]">
                                                <ChampionIconWithFallback
                                                    className="h-full w-full scale-[1.18] rounded-full object-cover"
                                                    src={buildSearchChampionIconSrc(championResultsMeta?.champion?.name_id)}
                                                    alt={`${championResultsMeta?.champion?.name} icon`}
                                                />
                                            </div>
                                            <div className="min-w-0">
                                                <div className="truncate text-sm font-semibold text-zinc-200">
                                                    {championResultsMeta?.champion?.name}
                                                </div>
                                                <div className="text-[11px] text-zinc-400">
                                                    {formatSearchMasteryPoints(result.champion_mastery_points)}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="hidden shrink-0 md:block md:w-[136px] md:justify-self-end">
                                        {isRanked ? (
                                            <div className="flex items-center justify-end gap-2">
                                            <div className="min-w-0 text-right">
                                                    <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-zinc-100">
                                                    {eloLabel}
                                                    </div>
                                                    <div className="mt-0.5 text-[11px] text-zinc-300">
                                                        {result.competitive.league_points} LP
                                                    </div>
                                            </div>
                                            <img
                                                    className="h-10 w-10 shrink-0 object-contain"
                                                src={buildSearchTierIconSrc(result.competitive?.tier)}
                                                            alt={`${result.competitive?.tier || 'Unranked'} tier emblem`}
                                            />
                                        </div>
                                        ) : (
                                            <div className="text-right text-[11px] font-semibold uppercase tracking-[0.1em] text-zinc-300">
                                                Unranked
                                            </div>
                                        )}
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                ) : null}

                {!loading && !error && searchMode === 'champions' && (championResultsMeta?.total_pages || 0) > 1 ? (
                    <div className="flex items-center justify-center gap-2 pt-2">
                        <button
                            type="button"
                            className="inline-flex min-h-[36px] items-center justify-center rounded-md border border-zinc-700 bg-zinc-900 px-3 text-xs font-semibold uppercase tracking-[0.16em] text-zinc-200 transition duration-150 hover:border-zinc-600 hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-40"
                            disabled={currentPage <= 1}
                            onClick={() => navigateToSearchResults(trimmedQuery, displayRegion, 'champions', currentPage - 1)}
                        >
                            Prev
                        </button>
                        <div className="text-sm text-zinc-400">
                            Page {currentPage} / {championResultsMeta.total_pages}
                        </div>
                        <button
                            type="button"
                            className="inline-flex min-h-[36px] items-center justify-center rounded-md border border-zinc-700 bg-zinc-900 px-3 text-xs font-semibold uppercase tracking-[0.16em] text-zinc-200 transition duration-150 hover:border-zinc-600 hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-40"
                            disabled={currentPage >= championResultsMeta.total_pages}
                            onClick={() => navigateToSearchResults(trimmedQuery, displayRegion, 'champions', currentPage + 1)}
                        >
                            Next
                        </button>
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export default SearchResults;
